import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Paperclip, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, EmptyState, PageHeader, Pagination, Skeleton, Table, Td, Th } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';
import { fmtDate, fmtEgp } from '../../lib/format';
import { useSocketEvent } from '../../lib/socket';
import { invoiceTone, type InvoiceRow, type InvoiceSummary } from './types';

const TABS = ['', 'DRAFT', 'SENT', 'OVERDUE', 'PAID', 'CANCELLED'] as const;

export function InvoicesPage() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const q = useQuery({
    queryKey: ['invoices', page, search, status],
    queryFn: () => api<Paginated<InvoiceRow> & { meta: { sumTotalEgp: number; sumPaidEgp: number } }>(`/invoices${qs({ page, q: search, status: status || undefined })}`),
  });
  const summary = useQuery({ queryKey: ['invoices', 'summary'], queryFn: () => api<InvoiceSummary>('/invoices/summary') });
  useSocketEvent('invoice.updated', useCallback(() => void qc.invalidateQueries({ queryKey: ['invoices'] }), [qc]));
  const lang = i18n.language;
  const s = summary.data;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('nav.invoices')}
        subtitle={q.data ? t('common.totalCount', { count: q.data.meta.total }) : undefined}
        actions={
          <Can perm="invoices:create">
            <button className="btn-primary" onClick={() => nav('/invoices/new')}>
              <Plus size={16} /> {t('invoices.new')}
            </button>
          </Can>
        }
      />
      {s ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi label={t('invoices.kpi.outstanding')} value={fmtEgp(s.outstandingEgp, lang)} hint={t('common.totalCount', { count: s.outstandingCount })} onClick={() => setParams({ status: 'SENT' })} />
          <Kpi label={t('invoices.kpi.overdue')} value={fmtEgp(s.overdueEgp, lang)} hint={t('common.totalCount', { count: s.overdueCount })} tone={s.overdueCount ? 'danger' : undefined} onClick={() => setParams({ status: 'OVERDUE' })} />
          <Kpi label={t('invoices.kpi.paidMonth')} value={fmtEgp(s.paidMonthEgp, lang)} hint={t('common.totalCount', { count: s.paidMonthCount })} onClick={() => setParams({ status: 'PAID' })} />
          <Kpi label={t('invoices.kpi.drafts')} value={String(s.draftCount)} onClick={() => setParams({ status: 'DRAFT' })} />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-xl bg-surface-2 p-1" role="tablist">
          {TABS.map((st) => (
            <button key={st} role="tab" aria-selected={status === st} className={clsx('min-h-9 rounded-lg px-3 text-sm font-semibold transition-colors', status === st ? 'bg-surface text-brand-ink shadow-sm' : 'text-text-muted hover:text-text')} onClick={() => { setParams(st ? { status: st } : {}); setPage(1); }}>
              {st ? t(`invoices.statuses.${st}`) : t('common.all')}
            </button>
          ))}
        </div>
        <input className="input max-w-xs" placeholder={t('invoices.searchPlaceholder')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} aria-label={t('common.search')} />
        {q.data ? <span className="ms-auto text-sm text-text-muted">{t('invoices.total')}: <strong className="text-text">{fmtEgp(q.data.meta.sumTotalEgp, lang)}</strong> · {t('invoices.paid')}: <strong className="text-success-fg">{fmtEgp(q.data.meta.sumPaidEgp, lang)}</strong></span> : null}
      </div>
      {q.isLoading ? (
        <Skeleton className="h-72" />
      ) : !q.data?.data.length ? (
        <div className="card"><EmptyState title={t('invoices.empty')} hint={t('invoices.emptyHint')} action={<Can perm="invoices:create"><Link className="btn-primary" to="/invoices/new"><Plus size={16} /> {t('invoices.new')}</Link></Can>} /></div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('invoices.number')}</Th>
              <Th>{t('invoices.customer')}</Th>
              <Th>{t('invoices.trip')}</Th>
              <Th>{t('invoices.issueDate')}</Th>
              <Th>{t('invoices.dueDate')}</Th>
              <Th className="text-end">{t('invoices.total')}</Th>
              <Th className="text-end">{t('invoices.balance')}</Th>
              <Th sticky="end">{t('invoices.status')}</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.data.map((inv) => {
              const balance = Number(inv.totalEgp) - Number(inv.paidEgp);
              const late = inv.status === 'OVERDUE' || (inv.status === 'SENT' && !!inv.dueDate && new Date(inv.dueDate) < new Date());
              return (
                <tr key={inv.id} className="group hover:bg-surface-hover">
                  <Td>
                    <Link to={`/invoices/${inv.id}`} className="font-bold text-brand-ink hover:underline" dir="ltr">{inv.number}</Link>
                    {inv.uploadedFileId ? <Paperclip size={12} className="ms-1 inline text-text-faint" aria-label={t('invoices.uploaded')} /> : null}
                  </Td>
                  <Td>{inv.customer.name}</Td>
                  <Td>{inv.trip ? <Link to={`/trips/${inv.trip.id}`} className="hover:underline" dir="ltr">{inv.trip.number}</Link> : '—'}</Td>
                  <Td className="text-text-muted">{fmtDate(inv.issueDate, lang)}</Td>
                  <Td className={late ? 'font-semibold text-danger-fg' : 'text-text-muted'}>{fmtDate(inv.dueDate, lang)}</Td>
                  <Td className="text-end font-bold tabular-nums">{fmtEgp(inv.totalEgp, lang)}</Td>
                  <Td className="text-end tabular-nums">{inv.status === 'PAID' || inv.status === 'CANCELLED' ? '—' : fmtEgp(balance, lang)}</Td>
                  <Td sticky="end"><Badge tone={invoiceTone(inv.status)} dot>{t(`invoices.statuses.${inv.status}`)}</Badge></Td>
                </tr>
              );
            })}
          </tbody>
          <tfoot><tr><td colSpan={8}><Pagination page={q.data.meta.page} pageSize={q.data.meta.pageSize} total={q.data.meta.total} onPage={setPage} /></td></tr></tfoot>
        </Table>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, tone, onClick }: { label: string; value: string; hint?: string; tone?: 'danger'; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} className={clsx('card !p-4 text-start transition-colors hover:bg-surface-hover', tone === 'danger' ? 'border-danger/40 bg-danger-bg/40' : '')}>
      <div className="text-xs font-semibold text-text-muted">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
      {hint ? <div className="text-xs text-text-faint">{hint}</div> : null}
    </button>
  );
}
